/**
 * Manos del workshop: encontrar elementos, mover el scroll, tipear y clickear
 * de forma que React y Radix lo tomen como si fuera una persona.
 *
 * Sin React acá: son funciones puras sobre el DOM, para poder testear el
 * resolutor de targets con jsdom si hiciera falta.
 */
import type { Target } from './types';

const norm = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();

export function isVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return el.getClientRects().length > 0;
  if (el.getClientRects().length === 0) return false;
  const cs = getComputedStyle(el);
  return cs.visibility !== 'hidden' && cs.display !== 'none' && cs.opacity !== '0';
}

const CLICKABLE = 'button, [role="button"], [role="tab"], [role="option"], [role="menuitem"], [role="checkbox"], a, label, summary';

/** Diálogo abierto más reciente (el último en el DOM): lo que hay adentro gana. */
function openDialog(): Element | null {
  const dialogs = [...document.querySelectorAll('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')];
  return dialogs.length ? dialogs[dialogs.length - 1] : null;
}

export function findTarget(target: Target): HTMLElement | null {
  const pick = (els: Element[]): HTMLElement | null => {
    const visible = els.filter(isVisible) as HTMLElement[];
    if (!visible.length) return null;
    const dialog = openDialog();
    if (dialog) {
      const inside = visible.find((e) => dialog.contains(e));
      if (inside) return inside;
    }
    return visible[0];
  };

  if (target.startsWith('@')) {
    return pick([...document.querySelectorAll(`[data-tour="${CSS.escape(target.slice(1))}"]`)]);
  }
  if (target.startsWith('text:')) {
    const label = norm(target.slice(5));
    const all = [...document.querySelectorAll(CLICKABLE)];
    const exact = all.filter((e) => norm(e.textContent) === label);
    if (exact.length) return pick(exact);
    return pick(all.filter((e) => norm(e.textContent).includes(label)));
  }
  if (target.startsWith('row:')) {
    const label = norm(target.slice(4));
    return pick([...document.querySelectorAll('tr')].filter((e) => norm(e.textContent).includes(label)));
  }
  return pick([...document.querySelectorAll(target)]);
}

/** Centro del elemento en coordenadas de viewport (para el cursor guía, que es `fixed`). */
export function centerOf(el: Element): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return {
    x: Math.min(Math.max(r.left + r.width / 2, 8), window.innerWidth - 8),
    y: Math.min(Math.max(r.top + r.height / 2, 8), window.innerHeight - 8),
  };
}

/** true si hubo que scrollear (el llamador espera a que termine la animación). */
export function scrollIntoViewIfNeeded(el: Element, smooth: boolean): boolean {
  const r = el.getBoundingClientRect();
  const margin = 96;
  const inside = r.top >= margin && r.bottom <= window.innerHeight - margin;
  if (inside) return false;
  el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
  return true;
}

function pointerInit(el: Element, type: string): PointerEventInit & MouseEventInit {
  const { x, y } = centerOf(el);
  return {
    bubbles: true,
    cancelable: true,
    composed: true,
    clientX: x,
    clientY: y,
    button: 0,
    buttons: type.endsWith('down') ? 1 : 0,
    pointerId: 1,
    pointerType: 'mouse',
    isPrimary: true,
    view: window,
  };
}

/**
 * Clic "de persona": Radix abre Selects en pointerdown, activa Tabs en
 * mousedown y confirma opciones en pointerup/click según el pointerType; un
 * `el.click()` pelado no alcanza. Se despacha la secuencia completa.
 */
export function humanClick(el: Element): void {
  const seq: Array<[string, boolean]> = [
    ['pointermove', true],
    ['pointerdown', true],
    ['mousedown', false],
    ['pointerup', true],
    ['mouseup', false],
    ['click', false],
  ];
  for (const [type, pointer] of seq) {
    const init = pointerInit(el, type);
    el.dispatchEvent(pointer && 'PointerEvent' in window ? new PointerEvent(type, init) : new MouseEvent(type, init));
  }
}

/** Setea el valor como lo haría el teclado: React escucha el `input` nativo, no la propiedad. */
export function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

export function isTextField(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
}

/** Tecla global (Escape cierra los diálogos de Radix, que escuchan en document). */
export function pressKey(key: 'Escape' | 'Enter'): void {
  const target = document.activeElement ?? document.body;
  const init: KeyboardEventInit = { key, code: key, bubbles: true, cancelable: true };
  target.dispatchEvent(new KeyboardEvent('keydown', init));
  target.dispatchEvent(new KeyboardEvent('keyup', init));
}
